// src/features/academic/components/DailyQuiz.jsx
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import QuizQuestion from './QuizQuestion';
import QuizProgressBar from './QuizProgressBar';
import QuizRules from './QuizRules';

/**
 * Daily Quiz Component
 * Main component for the daily academic challenge quiz
 */
const DailyQuiz = ({ 
  quizState = {}, 
  onStart,
  onExit,
  onSelectAnswer,
  onSubmitAnswer,
  onNextQuestion,
  onShowRules,
  onHideRules
}) => {
  const {
    questions,
    currentQuestionIndex,
    userAnswers,
    timeLeft,
    isSubmitted,
    loading,
    error,
    hasCompletedToday,
    isQuizActive,
    userScore,
    showRules,
    QUIZ_TIME_LIMIT,
    QUESTIONS_PER_DAY,
    getCurrentQuestion,
    isAnswerCorrect,
    getCorrectAnswersCount,
    calculateScore
  } = quizState;
  
  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-8 text-center">
        <div className="animate-spin w-10 h-10 border-2 border-green-500 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-gray-600">Loading your challenge...</p>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-md p-8">
        <div className="text-center text-red-500 mb-4">
          <AlertTriangle size={40} className="mx-auto mb-2" />
          <h3 className="text-lg font-semibold">Error Loading Challenge</h3>
        </div>
        <p className="text-gray-600 text-center">{error}</p>
        <div className="mt-6 text-center">
          <button
            onClick={onExit}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  // Rules screen
  if (showRules && !isQuizActive && !hasCompletedToday) {
    return <QuizRules onStart={onStart} onCancel={onExit} />;
  }
  
  // Completed quiz state
  if (hasCompletedToday) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-md p-8"
      >
        <div className="text-center">
          <Trophy size={60} className="mx-auto mb-4 text-yellow-500" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Challenge Completed!</h2>
          <p className="text-gray-600 mb-6">You've already completed today's challenge.</p>
          
          <div className="inline-block bg-gradient-to-r from-green-500 to-teal-500 rounded-xl p-6 text-white">
            <div className="text-4xl font-bold mb-2">{userScore}</div>
            <div className="text-sm opacity-90">Today's Score</div>
          </div>
          
          <div className="mt-8 flex justify-center space-x-4">
            <button
              onClick={onExit}
              className="px-5 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={onShowRules}
              className="px-5 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              View Rules
            </button>
          </div>
        </div>
      </motion.div>
    );
  }
  
  // Quiz in progress
  if (isQuizActive) {
    const currentQuestion = getCurrentQuestion();
    
    if (!currentQuestion) {
      return (
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
          <AlertTriangle size={40} className="mx-auto mb-2 text-red-500" />
          <h3 className="text-lg font-semibold text-red-500">Question Not Found</h3>
          <p className="text-gray-600 mt-2">There was a problem loading the question.</p>
          <div className="mt-6">
            <button
              onClick={onExit}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Exit Quiz
            </button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="bg-white rounded-xl shadow-md p-4 pb-6">
        {/* Quiz Header */}
        <div className="mb-4 flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-800 flex items-center">
            <Trophy className="mr-2 text-green-500" size={20} />
            Daily Academic Challenge
          </h2>
          
          <button
            onClick={onExit}
            className="text-sm text-red-500 hover:text-red-700"
          >
            Exit Quiz
          </button>
        </div>
        
        {/* Progress Bar */}
        <QuizProgressBar
          currentIndex={currentQuestionIndex}
          totalQuestions={questions.length}
          userAnswers={userAnswers}
          questions={questions}
        />
        
        {/* Current Question */}
        <AnimatePresence mode="wait">
          <QuizQuestion
            key={currentQuestionIndex}
            question={currentQuestion.question}
            options={currentQuestion.options}
            selectedAnswer={userAnswers[currentQuestionIndex]}
            correctAnswer={currentQuestion.correctAnswer}
            isSubmitted={isSubmitted}
            timeLeft={timeLeft}
            totalTime={QUIZ_TIME_LIMIT}
            onSelectAnswer={onSelectAnswer}
            onSubmitAnswer={onSubmitAnswer}
            onTimeUp={onNextQuestion}
            questionNumber={currentQuestionIndex + 1}
            totalQuestions={questions.length}
            category={currentQuestion.category}
            difficulty={currentQuestion.difficulty}
          />
        </AnimatePresence>
      </div>
    );
  }
  
  // Default: Start screen
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white rounded-xl shadow-md p-8"
    >
      <div className="text-center">
        <Trophy size={60} className="mx-auto mb-4 text-green-500" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Daily Academic Challenge</h2>
        <p className="text-gray-600 mb-6">
          Test your knowledge with {QUESTIONS_PER_DAY} questions and earn points!
        </p>
        
        <div className="bg-green-50 rounded-lg p-4 mb-6 inline-block">
          <div className="font-medium text-green-800">Today's challenge is ready!</div>
          <div className="text-sm text-green-600 mt-1">Earn points for yourself and your university</div>
        </div>
        
        <div className="mt-4">
          <button
            onClick={onStart}
            className="px-6 py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white font-medium rounded-lg shadow-sm hover:from-green-600 hover:to-teal-600 transition-all"
          >
            Start Challenge
          </button>
          
          <div className="mt-4">
            <button
              onClick={onShowRules}
              className="text-sm text-green-600 hover:text-green-700"
            >
              View Rules
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default DailyQuiz;